// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Tile } from '@carbon/react';
import { formatKpiValue, type KgKpiValue } from '../../../utils/kgKpis';
import { STATUS_VARS } from '../../../utils/statusPresentation';

export interface KpiStripProps {
  kpis: KgKpiValue[];
}

/**
 * Site KPI tiles above the canvas.
 *
 * A tile with no instrumented points shows "—" and says why, rather than a
 * zero or an invented figure. Where a value *is* derived by summing or
 * averaging across assets, the contributing count is shown so an operator can
 * tell how much of the site the number actually covers.
 */
export const KpiStrip = ({ kpis }: KpiStripProps) => (
  <div className="sld-kpis" style={{ '--sld-kpi-count': kpis.length } as React.CSSProperties}>
    {kpis.map(kpi => {
      const unavailable = kpi.value === null;
      const caption = unavailable
        ? (kpi.unavailableReason ?? 'No instrumented points')
        : (kpi.def.caption ??
          `${kpi.contributingCount} ${kpi.contributingCount === 1 ? 'reading' : 'readings'}`);

      return (
        <Tile
          key={kpi.def.id}
          className="sld-kpi"
          data-testid="power-flow-kpi"
          data-kpi-id={kpi.def.id}
        >
          <div className="sld-kpi__head">
            <span
              className="sld-kpi__dot"
              style={{ background: STATUS_VARS[kpi.status] }}
              aria-hidden="true"
            />
            <span className="sld-kpi__label" data-testid="power-flow-kpi-label">
              {kpi.def.label}
            </span>
          </div>
          <p className="sld-kpi__value" data-testid="power-flow-kpi-value">
            {formatKpiValue(kpi)}
            {!unavailable && kpi.def.unit && <span className="sld-kpi__unit">{kpi.def.unit}</span>}
          </p>
          <p className="sld-kpi__caption">{caption}</p>
        </Tile>
      );
    })}
  </div>
);
